import * as v2 from '@/core/services/api/v2';

/**
 * O que a oficina faz para o pescador (1.2): apetrechos e poções.
 *
 * Separado da `exploration` porque o dono é outro: a squad equipa lá, o
 * pescador equipa aqui — e o caderno do pescador e a tela de pesca leem esta
 * store sem carregar a visão geral da exploração junto.
 *
 * Três regras, as três iguais às da exploração:
 * - **Nenhuma fórmula aqui.** O `resumo` (a soma dos apetrechos equipados) vem
 *   do servidor; a tela mostra.
 * - **O relógio é o do servidor.** `restaSegundos` chega contado lá; a store
 *   guarda o instante em que leu e a tela desconta o que passou desde então.
 *   Comparar `expiraEm` com o relógio do navegador já deu efeito "vencido ao
 *   nascer" nesta máquina.
 * - **O erro tem código.** `sem_efeito` é o que a tela traduz quando a poção de
 *   energia encontra a barra cheia.
 */

function codigoDoErro(error) {
  return error?.response?.data?.codigo || null;
}

const state = {
  /** `{ itens, equipados: { anzol, boia, caixa }, resumo }` — o que `GET gear/fishing` devolveu. */
  apetrechos: null,
  /** As poções no estoque. */
  pocoes: [],
  /** Os efeitos valendo, com `restaSegundos` e o instante da leitura. */
  efeitos: [],
  lidoEm: 0,
  carregando: false,
  /** `{ codigo, mensagem }` — o código é o que a tela traduz. */
  erro: null,
};

const mutations = {
  SET_APETRECHOS(state, apetrechos) {
    state.apetrechos = apetrechos;
  },
  SET_POCOES(state, pocoes) {
    state.pocoes = pocoes;
  },
  SET_EFEITOS(state, efeitos) {
    state.efeitos = efeitos;
    state.lidoEm = Date.now();
  },
  SET_CARREGANDO(state, valor) {
    state.carregando = valor;
  },
  SET_ERRO(state, erro) {
    state.erro = erro;
  },
};

async function executar({ commit }, fn) {
  commit('SET_ERRO', null);
  commit('SET_CARREGANDO', true);
  try {
    return await fn();
  } catch (error) {
    commit('SET_ERRO', {
      codigo: codigoDoErro(error),
      mensagem: error?.response?.data?.message || error?.message || 'erro',
    });
    throw error;
  } finally {
    commit('SET_CARREGANDO', false);
  }
}

const actions = {
  async carregarApetrechos({ commit, rootState }) {
    const playerId = rootState.player?.id;
    if (!playerId) return null;
    const apetrechos = await v2.apetrechos(playerId);
    commit('SET_APETRECHOS', apetrechos);
    return apetrechos;
  },

  /** Equipar devolve o baú inteiro: a store troca tudo, sem remarcar nada à mão. */
  async equiparApetrecho(contexto, playerFishingGearId) {
    const playerId = contexto.rootState.player?.id;
    const apetrechos = await executar(contexto, () => v2.equiparApetrecho(playerId, playerFishingGearId));
    contexto.commit('SET_APETRECHOS', apetrechos);
    return apetrechos;
  },

  async desequiparApetrecho(contexto, slot) {
    const playerId = contexto.rootState.player?.id;
    const apetrechos = await executar(contexto, () => v2.desequiparApetrecho(playerId, slot));
    contexto.commit('SET_APETRECHOS', apetrechos);
    return apetrechos;
  },

  async carregarPocoes({ commit, rootState }) {
    const playerId = rootState.player?.id;
    if (!playerId) return [];
    const [pocoes, efeitos] = await Promise.all([v2.pocoes(playerId), v2.efeitosDePocao(playerId)]);
    commit('SET_POCOES', pocoes);
    commit('SET_EFEITOS', efeitos);
    return pocoes;
  },

  /**
   * Beber. A poção de energia muda o perfil (a barra), então o jogador é
   * recarregado; as outras só mudam os efeitos e o estoque.
   */
  async beberPocao(contexto, potionId) {
    const playerId = contexto.rootState.player?.id;
    const resultado = await executar(contexto, () => v2.beberPocao(playerId, potionId));
    const recargas = [contexto.dispatch('carregarPocoes').catch(() => {})];
    if (resultado.efeito === 'energia') {
      recargas.push(contexto.dispatch('player/loadPlayerState', playerId, { root: true }).catch(() => {}));
    }
    await Promise.all(recargas);
    return resultado;
  },
};

const getters = {
  /** As unidades por slot, para os três seletores do caderno. */
  apetrechosPorSlot: (state) => (slot) =>
    (state.apetrechos?.itens || []).filter((i) => i.slot === slot),
  /**
   * O baú em PILHAS: uma por (catálogo, equipado).
   *
   * O servidor manda uma linha por unidade (`playerFishingGearId`), porque é a
   * unidade que se equipa. Mas três "Boia de Cortiça" sobrando são a mesma coisa
   * — não têm durabilidade nem nível — e a mochila e o mercado as mostram como
   * uma pilha "×3". A equipada fica numa pilha própria, com o selo: ela não é
   * vendável e o gesto dela é outro (guardar, não equipar).
   *
   * `unidades` guarda os ids na ordem do servidor (a mais antiga primeiro): equipar
   * uma pilha usa a primeira, que é a mesma que o mercado tiraria primeiro.
   */
  pilhasDeApetrechos: (state) => {
    const pilhas = new Map();
    for (const a of state.apetrechos?.itens || []) {
      const chave = `${a.fishingGearId}:${a.equipado ? 1 : 0}`;
      const pilha = pilhas.get(chave);
      if (pilha) {
        pilha.quantidade += 1;
        pilha.unidades.push(a.playerFishingGearId);
      } else {
        pilhas.set(chave, { ...a, chave, quantidade: 1, unidades: [a.playerFishingGearId] });
      }
    }
    return [...pilhas.values()];
  },
  /** O que está valendo agora, com os segundos já descontados desde a leitura. */
  efeitosAtivos: (state) => {
    const passados = Math.floor((Date.now() - state.lidoEm) / 1000);
    return state.efeitos
      .map((e) => ({ ...e, restaSegundos: Math.max(0, e.restaSegundos - passados) }))
      .filter((e) => e.restaSegundos > 0);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
